// councilModel.js — E4-WS2-T01 (refactor-deepening / F4, CouncilView pure logic).
//
// Pure logic pulled out of the CouncilView god-component: the peer-review
// SCORING (rankings + label mapping → per-author score matrix) with v2 ranking
// validation (self-rank excluded), the add-model dropdown's filter + provider
// grouping, and v2 history/comparison derivations. No React — testable directly.

/**
 * Borda-style scoring: each reviewer's ranked labels award N-idx points to the
 * author that label maps to. Returns { authorId: { reviewerId: score } }.
 *
 * v2 validation: a reviewer's own score is EXCLUDED (self-rank or a label that
 * maps back to the reviewer), unknown labels are skipped, and duplicate labels
 * only count once. Missing/invalid ranks never become manufactured points.
 *
 * @param {{agent_id: string, parsed_ranking: string[]}[]} rankings
 * @param {Record<string,string>} labelMapping  e.g. { A: 'opus', B: 'gpt4o' }
 */
export function rankingsToScores(rankings, labelMapping) {
  const scores = {}; // { authorId: { reviewerId: score } }
  const N = Object.keys(labelMapping || {}).length;
  for (const r of rankings || []) {
    if (Array.isArray(r?.parsed_ranking)) applyRanking(scores, r, labelMapping || {}, N);
  }
  return scores;
}

/**
 * Apply ONE reviewer's ranking to the score map. Each label awards (N - idx)
 * points to its mapped author; duplicate labels, unknown labels and the
 * reviewer's own entry are skipped (never manufactured into points).
 *
 * @param {Record<string, Record<string, number>>} scores
 * @param {{agent_id: string, parsed_ranking: string[]}} ranking
 * @param {Record<string,string>} labelMapping
 * @param {number} N
 */
function applyRanking(scores, ranking, labelMapping, N) {
  const seen = new Set();
  ranking.parsed_ranking.forEach((label, idx) => {
    const authorId = labelMapping[label];
    if (seen.has(label) || !authorId || authorId === ranking.agent_id) return;
    seen.add(label);
    if (!scores[authorId]) scores[authorId] = {};
    scores[authorId][ranking.agent_id] = N - idx;
  });
}

/** Average score per author across reviewers who ranked them (null when none). */
export function averageScores(scoresByAuthor) {
  const out = {};
  for (const [authorId, byReviewer] of Object.entries(scoresByAuthor || {})) {
    const values = Object.values(byReviewer).filter((v) => typeof v === 'number');
    out[authorId] = values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
  }
  return out;
}

/**
 * Order authors by average score (desc), authors without a score last. Used for
 * the comparison view. Stable by insertion order for ties.
 * @param {Record<string, {[reviewer: string]: number}>} scoresByAuthor
 * @returns {{agentId: string, avg: number|null, votes: number}[]}
 */
export function rankAuthors(scoresByAuthor) {
  const avgs = averageScores(scoresByAuthor);
  return Object.keys(scoresByAuthor || {})
    .map((agentId) => ({
      agentId,
      avg: avgs[agentId],
      votes: Object.keys(scoresByAuthor[agentId] || {}).length,
    }))
    .sort((a, b) => {
      if (a.avg == null && b.avg == null) return 0;
      if (a.avg == null) return 1;
      if (b.avg == null) return -1;
      return b.avg - a.avg;
    });
}

/** Filter the add-model list by a free-text query over id + provider + label. */
export function filterAvailableModels(available, search) {
  const q = (search || '').trim().toLowerCase();
  if (!q) return available;
  return available.filter((m) =>
    (m.id || '').toLowerCase().includes(q)
    || (m.provider || '').toLowerCase().includes(q)
    || (m.label || '').toLowerCase().includes(q),
  );
}

/** Group models by provider (order-preserving), for the shad-pro dropdown. */
export function groupModelsByProvider(models) {
  const groups = new Map();
  for (const m of models) {
    const key = m.provider || 'Outros';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(m);
  }
  return Array.from(groups.entries()).map(([provider, list]) => ({ provider, models: list }));
}

/**
 * Summary line for a history row (used by the history sidebar). Truthful about
 * status and cost.
 * @param {object} item
 * @returns {{title: string, subtitle: string}}
 */
export function historyItemSummary(item = {}) {
  const query = typeof item.query === 'string' ? item.query.trim() : '';
  const title = query.length > 90 ? `${query.slice(0, 90)}…` : (query || '(sem pergunta)');
  const agents = Array.isArray(item.agents) ? item.agents.length : 0;
  const when = typeof item.created_at === 'number' ? new Date(item.created_at).toLocaleString() : '';
  const parts = [item.status || 'complete', `${agents} conselheiros`];
  if (when) parts.push(when);
  return { title, subtitle: parts.join(' · ') };
}
